import React from "react";
import { type RouteComponentProps, withRouter } from "react-router";

import { faVideo } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCaretDown, faCaretUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { memoize, noop } from "lodash";
import moment from "moment";

import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { TeamsContext } from "~/integrations/teams/teamsContext";
import chromeExtensionPreview from "~/legacy-assets/images/extensions/chromeMeetExtension.png";
import firefoxExtensionPreview from "~/legacy-assets/images/extensions/firefoxMeetExtension.png";
import zoomExtensionPreview from "~/legacy-assets/images/extensions/zoomExtension.png";
import { ReactComponent as GoogleDriveIcon } from "~/legacy-assets/images/logos/google_drive.svg";
import { ReactComponent as HangoutsIcon } from "~/legacy-assets/images/logos/google_meet.svg";
import { ReactComponent as HangoutsWord } from "~/legacy-assets/images/logos/google_meet_word.svg";
import { ReactComponent as TeamsIcon } from "~/legacy-assets/images/logos/ms_teams.svg";
import { ReactComponent as WebexIcon } from "~/legacy-assets/images/logos/webex.svg";
import { ReactComponent as ZoomIcon } from "~/legacy-assets/images/logos/zoom_icon_only.svg";
import { ReactComponent as ZoomWordIcon } from "~/legacy-assets/images/logos/zoom_word.svg";
import {
	chromeStoreLink,
	isChrome,
	isFirefox,
	isTeams,
	isZoom,
	mozillaStoreLink,
	withQueryParams,
	zoomStoreLink,
} from "~/lib/utils";
import { ZOOM_APP_UPDATE_FEATURE_FLAG } from "~/settings/Integrations/featureFlag";
import { Button } from "~/ui";
import { Menu, MenuButton, MenuItems, MenuLink, MenuPopover } from "~/ui/dropdown";
import Checkbox from "~/ui/legacy/Checkbox";
import { Modal } from "~/ui/legacyFellowIndex";

import type {
	ConferenceCallProvider,
	AttachmentsOrConference_event$data as EVENT,
	MeetingAttachmentProvider,
} from "~/__generated__/AttachmentsOrConference_event.graphql";
import type { NoteCard_viewer$data as NoteViewer } from "~/__generated__/NoteCard_viewer.graphql";

import styles from "./VideoCallButton.module.css";

export function callName(id: ConferenceCallProvider): string | null {
	if (id === "MS_TEAMS") {
		return "Teams Meeting";
	} else if (id === "ZOOM") {
		return "Zoom Conference";
	} else if (id === "HANGOUTS_MEET") {
		return "Google Meet";
	} else if (id === "SKYPE_FOR_BUSINESS") {
		return "Skype Call";
	} else if (id === "WEBEX") {
		return "Webex Meeting";
	} else {
		return null;
	}
}

export function CallIcon({ provider }: { provider: ConferenceCallProvider }) {
	return (
		<>
			{provider === "ZOOM" ? (
				<ZoomIcon />
			) : provider === "HANGOUTS_MEET" ? (
				<HangoutsIcon />
			) : provider === "MS_TEAMS" ? (
				<TeamsIcon />
			) : provider === "WEBEX" ? (
				<WebexIcon />
			) : (
				<FontAwesomeIcon icon={faVideo} className={styles.faIcon} />
			)}
		</>
	);
}

export function RecordingsIcon({ provider }: { provider: MeetingAttachmentProvider }) {
	return (
		<>
			{provider === "ZOOM" ? (
				<ZoomIcon />
			) : provider === "GOOGLE" ? (
				<GoogleDriveIcon />
			) : (
				<FontAwesomeIcon icon={faVideo} className={styles.faIcon} />
			)}
		</>
	);
}

/** Content for a button or link: an icon and text for a given call provider */
export function CallText({ provider }: { provider: ConferenceCallProvider }) {
	return (
		<>
			<CallIcon provider={provider} />
			<p className={styles.name}>Join {callName(provider) ?? "Conference"}</p>
		</>
	);
}

export type CALL = EVENT["conferenceCalls"][number];
export function maybeOpenCompanionMode(eventGuid: string, call: CALL) {
	const disableCompanionModeForTheseProviders =
		window?.electron?.settingsAPI?.get("disableCompanionModeForTheseProviders") ?? [];
	const useCompanionFromNoteHeader = window?.electron?.settingsAPI?.get("useCompanionFromNoteHeader") ?? false;
	if (useCompanionFromNoteHeader && !disableCompanionModeForTheseProviders.includes(call.provider)) {
		window.electron?.navigateCompanionMode?.(`/meetings/${eventGuid}/`);
	}
}

type Props = RouteComponentProps & {
	event: EVENT;
	viewer: NoteViewer;
};

const GOOGLE_MEET_KEY = "VideoCallButton__googleMeet";
const ZOOM_KEY_NEW_SCOPE = "VideoCallButton__zoom__newScope";

export default withRouter(function VideoCallButton(props: Props) {
	const [modalOpen, setModalOpen] = React.useState(false);
	const [modalOpenedBefore, setModalOpenedBefore] = React.useState(false);
	const [checked, setChecked] = React.useState(false);
	const { hasDismissed, dismiss } = React.useContext(DismissibleElementsContext);
	const teamsContext = React.useContext(TeamsContext);

	const { event, viewer } = props;
	const { conferenceCalls } = event;
	const end = React.useMemo(() => (event.end ? new Date(event.end) : null), [event]);

	const meetCall = conferenceCalls.find(call => call.provider === "HANGOUTS_MEET");
	const zoomCall = conferenceCalls.find(call => call.provider === "ZOOM");

	let showExtensionPrompts =
		!viewer.lastUsedBrowserExtension && !hasDismissed(GOOGLE_MEET_KEY) && meetCall && (isChrome || isFirefox);

	const zoomAppUpdateFlagIsOn = useFeatureIsOn(ZOOM_APP_UPDATE_FEATURE_FLAG);
	const shouldUpdateScopes = Boolean(
		viewer.lastUsedZoomApp &&
			zoomCall &&
			!isZoom &&
			!viewer.integrations.zoom?.hasLocalRecordingTokenScope &&
			zoomAppUpdateFlagIsOn,
	);
	let showZoomPrompts =
		((!viewer.lastUsedZoomApp && zoomCall && !isZoom) || shouldUpdateScopes) && !hasDismissed(ZOOM_KEY_NEW_SCOPE);

	const now = new Date();
	const hasEnded = end && end < now;
	const oneDayPassed = !!hasEnded && moment(end) < moment().subtract(1, "day");

	const openCall = React.useMemo(
		() =>
			memoize((call: CALL, showModal?: boolean) => (e: React.MouseEvent) => {
				if (showModal) {
					if (
						(call.provider === "HANGOUTS_MEET" && !hasDismissed(GOOGLE_MEET_KEY) && !showZoomPrompts) ||
						(call.provider === "ZOOM" && !hasDismissed(ZOOM_KEY_NEW_SCOPE))
					) {
						setModalOpen(true);
						setModalOpenedBefore(true);
						e.preventDefault();
						return;
					}
				}

				if (isTeams && call.provider === "MS_TEAMS" && teamsContext) {
					teamsContext.executeDeepLink(call.url);
					e.preventDefault();
					return;
				}

				const isModifierActive = ["Alt", "AltGraph", "Control", "Hyper", "Meta", "OS", "Shift", "Super"].some(key =>
					e.getModifierState(key),
				);

				if (isModifierActive) return;

				e.preventDefault();

				const w = window.screen.availWidth / 2;
				const h = window.screen.availHeight / 2;

				const windowFeatures = `width=${w},height=${h}`;

				maybeOpenCompanionMode(event.guid, call);
				window.open(call.url, "_blank", windowFeatures);
			}),
		[hasDismissed, event.guid, showZoomPrompts, teamsContext],
	);

	const openExtensionStore = React.useCallback(() => {
		setModalOpen(false);
		dismiss(GOOGLE_MEET_KEY);

		/* eslint-disable camelcase */
		if (isFirefox)
			window.open(withQueryParams(mozillaStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
		else window.open(withQueryParams(chromeStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
		/* eslint-enable camelcase */
	}, [dismiss]);

	const openZoomMarketPlace = React.useCallback(() => {
		setModalOpen(false);
		dismiss(ZOOM_KEY_NEW_SCOPE);
		/* eslint-disable-next-line camelcase */
		window.open(withQueryParams(zoomStoreLink, { utm_content: "video_call_button" }), "_blank", "noopener");
	}, [dismiss]);

	const updateZoomScopes = React.useCallback(() => {
		setModalOpen(false);
		window.open(`${window.location.origin}/zoom/oauth`, "_blank", "noopener");
	}, []);

	const onMeetModalClose = React.useCallback(() => {
		setModalOpen(false);
		if (checked) dismiss(GOOGLE_MEET_KEY);
	}, [checked, dismiss]);

	const onZoomModalClose = React.useCallback(() => {
		setModalOpen(false);
		if (checked) dismiss(ZOOM_KEY_NEW_SCOPE);
	}, [checked, dismiss]);

	const onJoinFromModal = (call: CALL) => () => {
		if (call.provider === "HANGOUTS_MEET") {
			showExtensionPrompts = false;
			onMeetModalClose();
		}
		if (call.provider === "ZOOM") {
			showZoomPrompts = false;
			onZoomModalClose();
		}

		const w = window.screen.availWidth / 2;
		const h = window.screen.availHeight / 2;

		const windowFeatures = `width=${w},height=${h}`;

		maybeOpenCompanionMode(event.guid, call);
		window.open(call.url, "_blank", windowFeatures);
	};

	const onCheckboxChange = React.useCallback(() => {
		setChecked(checked => !checked);
	}, []);

	if (conferenceCalls.length > 0) {
		const meetModalContent = (
			<Modal isOpen={modalOpen} onClose={onMeetModalClose} width="42.25rem">
				<Modal.Body className={styles.modalContent}>
					<HangoutsWord className={styles.meetLogo} />
					<div className={styles.modalText}>
						Our users love having meeting agendas side-by-side in Google Meet using Fellow&apos;s{" "}
						{isFirefox ? "Firefox" : "Chrome"} extension.
					</div>
					<img className={styles.extensionPreview} src={isFirefox ? firefoxExtensionPreview : chromeExtensionPreview} />
					<div className={styles.subText}>
						Adding the extension takes less than 30 seconds. You can remove it at any time.
					</div>
				</Modal.Body>
				<Modal.Footer>
					{modalOpenedBefore && (
						<Modal.FooterSecondaryActions className={styles.footerSecondaryActions}>
							<Checkbox checked={checked} onChange={onCheckboxChange} />
							Don&rsquo;t show me this again
						</Modal.FooterSecondaryActions>
					)}
					<Button size="normal" onClick={meetCall ? onJoinFromModal(meetCall) : noop}>
						Join Google Meet
					</Button>
					<Button onClick={openExtensionStore} size="normal" variant="primary">
						Add extension
					</Button>
				</Modal.Footer>
			</Modal>
		);

		const zoomModalContent = (
			<Modal isOpen={modalOpen} onClose={onZoomModalClose} width="42.25rem">
				<Modal.Body className={styles.modalContent}>
					<ZoomWordIcon className={styles.zoomLogo} />
					<div className={styles.modalText}>
						{shouldUpdateScopes
							? "Fellow's Zoom App just got better! Update it to take advantage of the improved recording features."
							: `Use Fellow's Zoom App for seamless access to meeting notes right in your calls, and enhanced recording
					features.`}
					</div>
					<img className={styles.extensionPreview} src={zoomExtensionPreview} />
					<div className={styles.subText}>Never switch windows again - take notes directly in Zoom</div>
				</Modal.Body>
				<Modal.Footer>
					{modalOpenedBefore && (
						<Modal.FooterSecondaryActions className={styles.footerSecondaryActions}>
							<Checkbox checked={checked} onChange={onCheckboxChange} />
							Don&rsquo;t ask me again
						</Modal.FooterSecondaryActions>
					)}
					<Button size="normal" onClick={zoomCall ? onJoinFromModal(zoomCall) : noop} className={styles.joinButton}>
						Join Zoom
					</Button>
					<Button
						size="normal"
						onClick={shouldUpdateScopes ? updateZoomScopes : openZoomMarketPlace}
						className={styles.extensionButton}
						variant="primary"
					>
						{shouldUpdateScopes ? "Update Zoom App" : "Add Zoom App"}
					</Button>
				</Modal.Footer>
			</Modal>
		);

		if (oneDayPassed) {
			return null;
		}

		if (conferenceCalls.length > 1) {
			if (showExtensionPrompts || showZoomPrompts) {
				return (
					<>
						{showZoomPrompts ? zoomModalContent : meetModalContent}
						<Menu>
							{({ isExpanded }) => (
								<>
									<MenuButton
										className={cx(styles.button, {
											[styles.open]: isExpanded,
										})}
									>
										<FontAwesomeIcon icon={faVideo} className={styles.faIcon} />
										<p className={styles.name}>Join Conference</p>
										<FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} className={styles.faIcon} />
									</MenuButton>
									<MenuPopover>
										<MenuItems className={styles.menuItems}>
											{conferenceCalls.map(call => (
												<MenuLink
													key={call.url}
													href={call.url}
													onClick={openCall(call, true)}
													target="_blank"
													rel="noopener noreferrer"
													className={cx(styles.button, styles.menuItem)}
												>
													<CallText provider={call.provider} />
												</MenuLink>
											))}
										</MenuItems>
									</MenuPopover>
								</>
							)}
						</Menu>
					</>
				);
			} else {
				return (
					<Menu>
						{({ isExpanded }) => (
							<>
								<MenuButton className={cx(styles.button, { [styles.open]: isExpanded })}>
									<FontAwesomeIcon icon={faVideo} className={styles.faIcon} />
									<p className={styles.name}>Join Conference</p>
									<FontAwesomeIcon icon={isExpanded ? faCaretUp : faCaretDown} className={styles.faIcon} />
								</MenuButton>
								<MenuPopover>
									<MenuItems className={styles.menuItems}>
										{conferenceCalls.map(call => (
											<MenuLink
												key={call.url}
												href={call.url}
												onClick={openCall(call)}
												target="_blank"
												rel="noopener noreferrer"
												className={cx(styles.button, styles.menuItem)}
											>
												<CallText provider={call.provider} />
											</MenuLink>
										))}
									</MenuItems>
								</MenuPopover>
							</>
						)}
					</Menu>
				);
			}
		} else {
			const call = conferenceCalls[0];
			if (showExtensionPrompts || showZoomPrompts) {
				return (
					<>
						{call.provider === "ZOOM" ? zoomModalContent : meetModalContent}
						<a
							href={call.url}
							onClick={openCall(call, true)}
							target="_blank"
							rel="noopener noreferrer"
							className={styles.button}
						>
							<CallText provider={call.provider} />
						</a>
					</>
				);
			} else {
				return (
					<a
						href={call.url}
						onClick={openCall(call)}
						target="_blank"
						rel="noopener noreferrer"
						className={styles.button}
					>
						<CallText provider={call.provider} />
					</a>
				);
			}
		}
	} else {
		return null;
	}
});
